import { medisApi } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { unwrapMedis, type MedisEnvelope } from '@/lib/medis/envelope';
import { MEDIS_V2_ROUTES } from '@/lib/medis/v2'; // v1<->v2 switch, see lib/medis/v2/client.ts
import {
  detachPatientV2,
  getPatientRefreshV2,
  getPatientsV2,
  registerPatientV2,
} from './patients.v2.api'; // v2 when switched on
import { sha256Hex } from '@/lib/utils/hash';
import { birthYearFromCnp } from '../lib/cnp';
import type {
  AddPatientInput,
  AddPatientResult,
  AzurePatientDto,
  DetachPatientInput,
  DetachPatientResult,
  GetPatientResponseDto,
  Patient,
  PatientRefresh,
} from '../types/patient.types';

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function mapPatient(dto: AzurePatientDto, isPrimary: boolean): Patient | null {
  if (!dto.FhirID) return null; // a patient without a FHIR id can't be used downstream
  return {
    fhirId: dto.FhirID,
    medisId: dto.MedisID ?? null,
    firstName: dto.FirstName ?? '',
    lastName: dto.LastName ?? '',
    fullName: [dto.FirstName, dto.LastName].filter(Boolean).join(' ').trim(),
    birthDay: parseDate(dto.BirthDay),
    age: String(dto.Age ?? ''),
    isPrimary,
    attachedBy: dto.AttachedBy ?? [],
    actCode: dto.ActCode ?? null,
  };
}

export const patientsApi = {
  /**
   * Lists the account holder plus any attached dependents.
   * `GET /GetPatient` — session headers are attached by the medisApi interceptor.
   */
  async getPatients(): Promise<Patient[]> {
    if (MEDIS_V2_ROUTES.getPatient) return getPatientsV2(); // v2 when switched on

    const { data } =
      await medisApi.get<MedisEnvelope<GetPatientResponseDto>>(endpoints.medis.getPatient);
    const response = unwrapMedis(data);

    const patients: Patient[] = [];
    if (response.Patient) {
      const primary = mapPatient(response.Patient, true);
      if (primary) patients.push(primary);
    }
    for (const attached of response.PatientsAttached ?? []) {
      const mapped = mapPatient(attached, false);
      if (mapped) patients.push(mapped);
    }
    return patients;
  },

  /**
   * Fresh attach code + `attachedBy` of the primary patient — v2 only
   * (`GET /v2/GetPatientRefresh`), called on every entry to the patients page.
   * `null` = nothing to refresh (204 — no registered patient). The cached
   * patient list is the fallback whenever this call fails (see
   * `usePatientRefresh`).
   */
  refreshPatient(): Promise<PatientRefresh | null> {
    return getPatientRefreshV2();
  },

  /**
   * Registers or attaches a patient ("Adaugă pacient") — v2 only, no v1 fallback.
   *
   * `POST /v2/RegisterPatient` with `{ ssnHash, uniqueCode, birthYear, attachPatient }`.
   * `ssnHash` is sha256(CNP); `birthYear` is derived from the CNP; `attachPatient`
   * is false only for the first patient on the account (register mode — the
   * response then also carries `AttachedAt`).
   */
  async registerPatient({
    cnp,
    activationCode,
    attach = true,
  }: AddPatientInput): Promise<AddPatientResult> {
    const birthYear = birthYearFromCnp(cnp);
    if (birthYear === null) {
      return { success: false, outcome: 'invalidSsn' };
    }
    const ssnHash = await sha256Hex(cnp);
    return registerPatientV2({
      ssnHash,
      uniqueCode: activationCode,
      birthYear,
      attachPatient: attach,
    });
  },

  /**
   * Removes the association between a patient and one of the accounts in its
   * `attachedBy` list — v2 only. ⚠️ Server endpoint still under construction
   * (answers 501 until it ships); see `detachPatientV2`.
   */
  detachPatient(input: DetachPatientInput): Promise<DetachPatientResult> {
    return detachPatientV2(input);
  },
};
